import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

// Ciudades predefinidas de España con coordenadas PRECISAS
const CITIES = {
  'Madrid': { lat: 40.4168, lng: -3.7038 },
  'Barcelona': { lat: 41.3851, lng: 2.1734 },
  'Valencia': { lat: 39.4699, lng: -0.3763 },
  'Sevilla': { lat: 37.3891, lng: -5.9845 },
  'Málaga': { lat: 36.7213, lng: -4.4214 },
  'Bilbao': { lat: 43.2630, lng: -2.9350 },
  'Alicante': { lat: 38.3452, lng: -0.4810 },
  'Zaragoza': { lat: 41.6488, lng: -0.8891 }
};

const AddressAutocomplete = ({ onChange, placeholder, dataTestId, value }) => {
  const [address, setAddress] = useState(value || '');
  const [showManual, setShowManual] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [useNativeWidget, setUseNativeWidget] = useState(false);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Initialize Google Places Autocomplete when available
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10; // Increased retries
    
    const initAutocomplete = () => {
      console.log('🔍 Attempting to initialize Autocomplete...', {
        hasGoogle: !!window.google,
        hasMaps: !!(window.google && window.google.maps),
        hasPlaces: !!(window.google && window.google.maps && window.google.maps.places),
        hasInputRef: !!inputRef.current,
        alreadyInitialized: !!autocompleteRef.current
      });
      
      if (window.google && window.google.maps && window.google.maps.places && inputRef.current && !autocompleteRef.current) {
        try {
          console.log('✅ All conditions met, creating Autocomplete instance...');
          autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['address']
          });

          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            
            if (place.geometry && place.geometry.location) {
              const selectedAddress = place.formatted_address || place.name;
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              
              setAddress(selectedAddress);
              onChange({
                address: selectedAddress,
                lat: lat,
                lng: lng
              });
              toast.success('✓ Dirección seleccionada correctamente');
            } else {
              toast.error('No se pudieron obtener las coordenadas de esta dirección');
            }
          });
          
          // Prevent form submission on Enter in autocomplete dropdown
          if (inputRef.current) {
            inputRef.current.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            });
            
            // FIX: Prevent input blur when clicking on autocomplete dropdown
            // This is a known issue with Google Places Autocomplete
            inputRef.current.addEventListener('mousedown', (e) => {
              // Allow the click to go through to the dropdown
              e.stopPropagation();
            });
          }
          
          console.log('✅ Autocomplete initialized successfully');
          setUseNativeWidget(true); // Mark that native widget is working
          return true; // Success
        } catch (error) {
          console.error('❌ Error initializing Google Autocomplete:', error);
          toast.error('Error al inicializar autocompletado. Usa los botones de ciudad.');
          return false;
        }
      }
      return false; // Not ready yet
    };

    // Try to initialize immediately
    if (!initAutocomplete()) {
      // If not successful, retry with increasing delays
      const retryTimer = setInterval(() => {
        retryCount++;
        console.log(`🔄 Retry ${retryCount}/${maxRetries} - Waiting for Google Maps API...`);
        
        if (initAutocomplete()) {
          console.log('✅ Autocomplete initialized after retry');
          clearInterval(retryTimer);
        } else if (retryCount >= maxRetries) {
          console.warn('⚠️ Google Maps API not available after maximum retries. Address input will work without autocomplete.');
          clearInterval(retryTimer);
        }
      }, 1000);
      
      return () => clearInterval(retryTimer);
    }
  }, [onChange]);

  // Update local state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setAddress(value);
    }
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCitySelect = (city) => {
    const coords = CITIES[city];
    const cityAddress = city + ', España';
    setAddress(cityAddress);
    onChange({
      address: cityAddress,
      lat: coords.lat,
      lng: coords.lng
    });
    toast.success('✓ Ciudad seleccionada: ' + city);
  };

  const handleManualCoords = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      const coordsAddress = address || `Coordenadas: ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setAddress(coordsAddress);
      onChange({
        address: coordsAddress,
        lat: lat,
        lng: lng
      });
      toast.success('✓ Coordenadas establecidas correctamente');
      setShowManual(false);
      setManualLat('');
      setManualLng('');
    } else {
      toast.error('Coordenadas inválidas. Lat: -90 a 90, Lng: -180 a 180');
    }
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setAddress(input);
    
    // Detect Google Maps links
    if (input.includes('google.com/maps') || input.includes('goo.gl/maps') || input.includes('maps.app.goo.gl')) {
      extractCoordinatesFromLink(input);
      setShowSuggestions(false);
      return;
    }
    
    // ONLY use manual fallback if native widget is NOT working
    if (!useNativeWidget && input.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: input,
          types: ['address']
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            console.log('📍 Manual autocomplete found', predictions.length, 'suggestions');
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            console.log('❌ Manual autocomplete status:', status);
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } else {
      // If native widget is working, hide manual suggestions
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const extractCoordinatesFromLink = async (link) => {
    try {
      // Extract coordinates from Google Maps link
      // Format 1: https://www.google.com/maps?q=41.3851,2.1734
      // Format 2: https://www.google.com/maps/@41.3851,2.1734,15z
      // Format 3: https://maps.google.com/?q=41.3851,2.1734
      
      let lat, lng;
      
      // Try to find @lat,lng pattern
      const atPattern = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
      const atMatch = link.match(atPattern);
      
      if (atMatch) {
        lat = parseFloat(atMatch[1]);
        lng = parseFloat(atMatch[2]);
      } else {
        // Try to find q=lat,lng pattern
        const qPattern = /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
        const qMatch = link.match(qPattern);
        
        if (qMatch) {
          lat = parseFloat(qMatch[1]);
          lng = parseFloat(qMatch[2]);
        }
      }
      
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        // Valid coordinates found
        onChange({
          address: `Coordenadas desde Google Maps: ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
          lat: lat,
          lng: lng
        });
        setAddress(`Google Maps: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        toast.success('✓ Coordenadas extraídas del enlace de Google Maps');
      } else {
        toast.error('No se pudieron extraer coordenadas del enlace');
      }
    } catch (error) {
      console.error('Error extracting coordinates from link:', error);
    }
  };

  const tryGeocoding = () => {
    // Trigger Google Places autocomplete programmatically
    if (autocompleteRef.current && window.google && window.google.maps.places) {
      // If autocomplete has a selected place, use it
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const selectedAddress = place.formatted_address || place.name;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setAddress(selectedAddress);
        onChange({
          address: selectedAddress,
          lat: lat,
          lng: lng
        });
        toast.success('✓ Dirección seleccionada correctamente');
      } else {
        toast.info('Por favor selecciona una dirección de las sugerencias');
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    console.log('📍 Suggestion clicked:', suggestion);
    setShowSuggestions(false);
    
    // Use PlacesService to get place details including coordinates
    if (window.google && window.google.maps && window.google.maps.places) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['formatted_address', 'geometry', 'name']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const selectedAddress = place.formatted_address || place.name;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            
            setAddress(selectedAddress);
            onChange({
              address: selectedAddress,
              lat: lat,
              lng: lng
            });
            toast.success('✓ Dirección seleccionada correctamente');
            console.log('✅ Address selected:', { selectedAddress, lat, lng });
          } else {
            toast.error('Error al obtener detalles de la dirección');
            console.error('❌ PlacesService error:', status);
          }
        }
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryGeocoding();
    }
  };

  return (
    <div className="space-y-3">
      {/* Address input field with Google Autocomplete */}
      <div className="space-y-2 relative">
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={handleAddressChange}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder || "Escribe dirección o pega link de Google Maps..."}
          data-testid={dataTestId}
          autoComplete="off"
          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        
        {/* Manual Suggestions Dropdown - Fallback if Google widget doesn't work */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="absolute z-[999999] w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
            style={{ top: '100%', left: 0 }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.place_id || index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm text-gray-900">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </div>
                {suggestion.structured_formatting?.secondary_text && (
                  <div className="text-xs text-gray-600">
                    {suggestion.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        <p className="text-xs text-gray-600">
          💡 Escribe la dirección, selecciona sugerencias o pega un enlace de Google Maps
        </p>
      </div>
      
      {/* Quick City Selection - ALTERNATIVE METHOD */}
      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
        <p className="text-xs font-medium text-green-800 mb-2">
          🏙️ O selecciona una ciudad:
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(CITIES).map(city => (
            <Button
              key={city}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleCitySelect(city)}
              className="text-xs border-green-300 hover:bg-green-100"
            >
              {city}
            </Button>
          ))}
        </div>
      </div>

      {/* Manual Coordinates - SECONDARY METHOD */}
      <div className="border-t pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowManual(!showManual)}
          className="w-full text-xs"
        >
          {showManual ? '✗ Cerrar Coordenadas' : '📍 O Usar Coordenadas GPS Manuales'}
        </Button>

        {showManual && (
          <div className="mt-2 space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs font-medium text-blue-800">
              Ingresa coordenadas GPS precisas:
            </p>
            <div className="space-y-2">
              <input
                type="number"
                step="0.0001"
                placeholder="Latitud (ej: 41.3851 para Barcelona)"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                step="0.0001"
                placeholder="Longitud (ej: 2.1734 para Barcelona)"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button
                type="button"
                size="sm"
                onClick={handleManualCoords}
                disabled={!manualLat || !manualLng}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
              >
                ✓ Establecer Coordenadas
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressAutocomplete;
